import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { eventService } from '../services/api';
import axios from 'axios';
import '../styles/AdminPage.css';
import '../styles/CustomFileInput.css';

const AdminPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [approvedEvents, setApprovedEvents] = useState([]);
  const [rejectedEvents, setRejectedEvents] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [approvedVerifications, setApprovedVerifications] = useState([]);
  const [rejectedVerifications, setRejectedVerifications] = useState([]);
  const validVerifFilters = ['pending', 'approved', 'rejected'];
  const [verifStatusFilter, setVerifStatusFilter] = useState('pending');
  const validTabs = ['events', 'verifications'];
  const [activeTab, setActiveTabState] = useState(() => {
    const tab = searchParams.get('tab');
    return validTabs.includes(tab) ? tab : 'events';
  });
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    setSearchParams({ tab });
  };
  const validStatusFilters = ['pending', 'approved', 'rejected'];
  const [eventStatusFilter, setEventStatusFilter] = useState(() => {
    const s = searchParams.get('status');
    return validStatusFilters.includes(s) ? s : 'pending';
  });
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({});
  const [newBannerImage, setNewBannerImage] = useState(null);
  const [removeBannerImage, setRemoveBannerImage] = useState(false);
  const [currentBannerImage, setCurrentBannerImage] = useState(null);
    const [rejectingEventId, setRejectingEventId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  const eventTypeLabels = {
    concert: 'Konzert',
    festival: 'Festival',
    sports: 'Sport',
    exhibition: 'Ausstellung',
    theater: 'Theater',
    workshop: 'Workshop',
    conference: 'Konferenz',
    party: 'Party',
    other: 'Andere'
  };

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchPendingEvents();
      fetchApprovedEvents();
      fetchRejectedEvents();
      fetchPendingVerifications();
      fetchApprovedVerifications();
      fetchRejectedVerifications();
    }
  }, [user]);

  const fetchPendingEvents = async () => {
    try {
      const { data } = await eventService.getPendingEvents();
      setPendingEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching pending events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedEvents = async () => {
    try {
      const { data } = await axios.get('/api/events/approved');
      setApprovedEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching approved events:', error);
    }
  };

  const fetchRejectedEvents = async () => {
    try {
      const { data } = await axios.get('/api/events/rejected');
      setRejectedEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching rejected events:', error);
    }
  };

  const fetchPendingVerifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/auth/pending-verifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingVerifications(data);
    } catch (error) {
      console.error('Error fetching pending verifications:', error);
    }
  };

  const fetchApprovedVerifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/auth/approved-verifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovedVerifications(data);
    } catch (error) {
      console.error('Error fetching approved verifications:', error);
    }
  };

  const fetchRejectedVerifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/auth/rejected-verifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRejectedVerifications(data);
    } catch (error) {
      console.error('Error fetching rejected verifications:', error);
    }
  };

  const handleReview = async (eventId, status, reason) => {
    try {
      const { data: updatedEvent } = await eventService.reviewEvent(eventId, status, reason);
      const event = pendingEvents.find(e => e._id === eventId);
      const updated = { ...(event || {}), ...updatedEvent, status };
      setPendingEvents(pendingEvents.filter(e => e._id !== eventId));
      if (status === 'approved') {
        setApprovedEvents(prev => [updated, ...prev]);
      } else {
        setRejectedEvents(prev => [updated, ...prev]);
      }
      setRejectingEventId(null);
      setRejectionReason('');
      const statusText = status === 'approved' ? 'genehmigt' : 'abgelehnt';
      showToast(`Event erfolgreich ${statusText}!`, 'success');
    } catch (error) {
      console.error('Error reviewing event:', error);
      showToast('Fehler beim Überprüfen des Events', 'error');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Event wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    try {
      await eventService.deleteEvent(eventId);
      setApprovedEvents(approvedEvents.filter(e => e._id !== eventId));
      showToast('Event erfolgreich gelöscht!', 'success');
    } catch (error) {
      console.error('Error deleting event:', error);
      showToast('Fehler beim Löschen des Events', 'error');
    }
  };

  const handleVerificationReview = async (userId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/auth/review-verification/${userId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const entry = pendingVerifications.find(u => u._id === userId);
      const updated = { ...(entry || {}), verificationStatus: status, isVerified: status === 'approved' };
      setPendingVerifications(prev => prev.filter(u => u._id !== userId));
      if (status === 'approved') {
        setApprovedVerifications(prev => [updated, ...prev]);
      } else {
        setRejectedVerifications(prev => [updated, ...prev]);
      }
      const statusText = status === 'approved' ? 'genehmigt' : 'abgelehnt';
      showToast(`Verifizierung erfolgreich ${statusText}!`, 'success');
    } catch (error) {
      console.error('Error reviewing verification:', error);
      showToast('Fehler beim Überprüfen der Verifizierung', 'error');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event._id);
    setCurrentBannerImage(event.bannerImage || null);
    setNewBannerImage(null);
    setRemoveBannerImage(false);
    setFormData({
      title: event.title,
      description: event.description,
      eventType: event.eventType,
      location: event.location,
      canton: event.canton,
      startDate: event.startDate.split('T')[0],
      startTime: event.startTime,
      endDate: event.endDate.split('T')[0],
      endTime: event.endTime,
      minimumAge: event.minimumAge,
      price: event.price,
      website: event.website
    });
  };

  const handleFileChange = (e) => {
    setNewBannerImage(e.target.files[0]);
    setRemoveBannerImage(false);
  };

  const handleRemoveImage = () => {
    setRemoveBannerImage(true);
    setNewBannerImage(null);
    setCurrentBannerImage(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdate = async (eventId) => {
    // Validate that end date/time is not before start date/time
    const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
    const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);

    if (endDateTime <= startDateTime) {
      showToast('Enddatum und -zeit müssen nach Startdatum und -zeit liegen', 'error');
      return;
    }

    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        data.append(key, formData[key]);
      });

      // Handle banner image
      if (newBannerImage) {
        data.append('bannerImage', newBannerImage);
      } else if (removeBannerImage) {
        data.append('removeBannerImage', 'true');
      }

      await eventService.updateEvent(eventId, data);
      setEditingEvent(null);
      setNewBannerImage(null);
      setRemoveBannerImage(false);
      setCurrentBannerImage(null);
      fetchPendingEvents();
      fetchApprovedEvents();
      showToast('Event erfolgreich aktualisiert!', 'success');
    } catch (error) {
      console.error('Error updating event:', error);
      showToast('Fehler beim Aktualisieren des Events', 'error');
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="admin-page">
        <p>Zugriff verweigert. Admin-Rechte erforderlich.</p>
      </div>
    );
  }

  if (loading) return <div className="loading">Daten werden geladen...</div>;

  const currentEvents = eventStatusFilter === 'pending' ? pendingEvents : eventStatusFilter === 'approved' ? approvedEvents : rejectedEvents;

  const renderVerificationCard = (userRequest, status = 'pending') => (
    <div key={userRequest._id} className="verification-request-card">
      <div className="verification-info">
        <div className="verification-header">
          <h3>{userRequest.organizerName || userRequest.profileName}</h3>
          <span className="request-date">
            Angefragt am: {new Date(userRequest.verificationRequestDate).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' })}
          </span>
        </div>

        <div className="verification-details">
          <div className="detail-item">
            <strong>E-Mail:</strong> {userRequest.email}
          </div>
          <div className="detail-item">
            <strong>Profilname:</strong> {userRequest.profileName}
          </div>
          {userRequest.organizerName && userRequest.organizerName !== userRequest.profileName && (
            <div className="detail-item">
              <strong>Veranstaltername:</strong> {userRequest.organizerName}
            </div>
          )}
          {userRequest.description && (
            <div className="detail-item description">
              <strong>Beschreibung:</strong>
              <span>{userRequest.description}</span>
            </div>
          )}
          {(userRequest.website || userRequest.instagram || userRequest.tiktok) && (
            <div className="detail-item links">
              <strong>Links:</strong>
              <div className="links-list">
                {userRequest.website && (
                  <a href={userRequest.website} target="_blank" rel="noopener noreferrer" title={userRequest.website}>
                    Webseite
                  </a>
                )}
                {userRequest.instagram && (
                  <a href={userRequest.instagram} target="_blank" rel="noopener noreferrer" title={userRequest.instagram}>
                    Instagram
                  </a>
                )}
                {userRequest.tiktok && (
                  <a href={userRequest.tiktok} target="_blank" rel="noopener noreferrer" title={userRequest.tiktok}>
                    TikTok
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="verification-actions">
        {status === 'pending' ? (
          <>
            <button
              onClick={() => handleVerificationReview(userRequest._id, 'approved')}
              className="btn btn-success btn-icon"
              title="Genehmigen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </button>
            <button
              onClick={() => handleVerificationReview(userRequest._id, 'rejected')}
              className="btn btn-danger btn-icon"
              title="Ablehnen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </>
        ) : (
          <span className={`status-badge status-badge--${status}`}>
            {status === 'approved' ? 'Genehmigt' : 'Abgelehnt'}
          </span>
        )}
      </div>
    </div>
  );

  const renderEventCard = (event) => (
    <div key={event._id} className="event-review-card">
      {editingEvent === event._id ? (
        <div className="edit-form">
          <h3>Event bearbeiten</h3>
          <div className="form-group">
            <label>Titel</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Beschreibung</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Event-Typ</label>
              <select name="eventType" value={formData.eventType} onChange={handleChange}>
                <option value="concert">Konzert</option>
                <option value="festival">Festival</option>
                <option value="sports">Sport</option>
                <option value="exhibition">Ausstellung</option>
                <option value="theater">Theater</option>
                <option value="workshop">Workshop</option>
                <option value="conference">Konferenz</option>
                <option value="party">Party</option>
                <option value="other">Andere</option>
              </select>
            </div>

            <div className="form-group">
              <label>Kanton</label>
              <select name="canton" value={formData.canton} onChange={handleChange}>
                <option value="">Kanton wählen</option>
                <option value="AG">Aargau</option>
                <option value="AI">Appenzell Innerrhoden</option>
                <option value="AR">Appenzell Ausserrhoden</option>
                <option value="BE">Bern</option>
                <option value="BL">Basel-Landschaft</option>
                <option value="BS">Basel-Stadt</option>
                <option value="FR">Freiburg</option>
                <option value="GE">Genf</option>
                <option value="GL">Glarus</option>
                <option value="GR">Graubünden</option>
                <option value="JU">Jura</option>
                <option value="LU">Luzern</option>
                <option value="NE">Neuenburg</option>
                <option value="NW">Nidwalden</option>
                <option value="OW">Obwalden</option>
                <option value="SG">St. Gallen</option>
                <option value="SH">Schaffhausen</option>
                <option value="SO">Solothurn</option>
                <option value="SZ">Schwyz</option>
                <option value="TG">Thurgau</option>
                <option value="TI">Tessin</option>
                <option value="UR">Uri</option>
                <option value="VD">Waadt</option>
                <option value="VS">Wallis</option>
                <option value="ZG">Zug</option>
                <option value="ZH">Zürich</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Ort</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Startdatum</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Startzeit</label>
              <input
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Enddatum</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Endzeit</label>
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Preis</label>
              <input
                type="text"
                name="price"
                value={formData.price}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Mindestalter</label>
              <input
                type="number"
                name="minimumAge"
                value={formData.minimumAge}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Webseite</label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Banner-Bild</label>
            {currentBannerImage && !removeBannerImage && !newBannerImage && (
              <div className="current-banner-preview">
                <img src={currentBannerImage} alt="Current banner" style={{ maxWidth: '300px', marginBottom: '10px', borderRadius: '5px' }} />
                <button type="button" onClick={handleRemoveImage} className="btn btn-danger" style={{ display: 'block', marginTop: '10px' }}>
                  Bild entfernen
                </button>
              </div>
            )}
            {(removeBannerImage || !currentBannerImage) && (
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                {removeBannerImage && <p style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px' }}>Bild wird beim Speichern entfernt</p>}
              </div>
            )}
            {newBannerImage && (
              <div style={{ marginTop: '10px' }}>
                <p style={{ color: '#28a745', fontSize: '14px' }}>Neues Bild ausgewählt: {newBannerImage.name}</p>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button onClick={() => handleUpdate(event._id)} className="btn btn-primary">
              Änderungen speichern
            </button>
            <button onClick={() => setEditingEvent(null)} className="btn btn-secondary">
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <div className="event-list-item">
          <div className="event-list-image">
            {event.bannerImage ? (
              <img src={event.bannerImage} alt={event.title} />
            ) : (
              <div className="placeholder-image">
                <span className="placeholder-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </span>
              </div>
            )}
          </div>

          <div className="event-list-content">
            <h3><Link to={`/event/${event._id}`} className="event-title-link">{event.title}</Link></h3>
            <div className="event-list-info">
              <span className="info-item">
                <span className="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </span>
                {event.location}, {event.canton}
              </span>
              <span className="info-item">
                <span className="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </span>
                {new Date(event.startDate).toLocaleDateString('de-CH')}
              </span>
              <span className="info-item">
                <span className="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                    <line x1="7" y1="7" x2="7.01" y2="7"></line>
                  </svg>
                </span>
                {eventTypeLabels[event.eventType] || event.eventType}
              </span>
              <span className="info-item">
                <span className="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </span>
                {event.updatedAt && event.updatedAt !== event.createdAt ? 'Überarbeitet' : 'Eingereicht'}: {new Date(event.updatedAt || event.createdAt).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' })}
              </span>
              <span className="info-item organizer">
                <span className="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </span>
                <Link to={`/organizer/${event.organizer?._id}`} className="organizer-link">
                  {event.organizer?.isVerified && event.organizer?.organizerName
                    ? event.organizer.organizerName
                    : (event.organizer?.profileName || event.organizer?.organizerName)}
                  {event.organizer?.isVerified && (
                    <span className="verified-badge-small" title="Verifizierter Veranstalter">✓</span>
                  )}
                </Link>
              </span>
            </div>
          </div>

          <div className="event-list-actions">
            <button onClick={() => navigate(`/event/${event._id}`)} className="btn btn-primary btn-sm btn-icon" title="Ansehen">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </button>
            <button onClick={() => handleEdit(event)} className="btn btn-secondary btn-sm btn-icon" title="Bearbeiten">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
            {eventStatusFilter === 'approved' && (
              <button onClick={() => handleDelete(event._id)} className="btn btn-danger btn-sm btn-icon" title="Löschen">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                  <path d="M10 11v6"></path>
                  <path d="M14 11v6"></path>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                </svg>
              </button>
            )}
            {eventStatusFilter === 'pending' && (
              <>
                <button onClick={() => handleReview(event._id, 'approved')} className="btn btn-success btn-sm btn-icon" title="Genehmigen">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </button>
                <button onClick={() => { setRejectingEventId(event._id); setRejectionReason(''); }} className="btn btn-danger btn-sm btn-icon" title="Ablehnen">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </>
            )}
            {rejectingEventId === event._id && (
              <div className="rejection-form">
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Begründung für die Ablehnung (optional)"
                  rows="3"
                  className="rejection-textarea"
                />
                <div className="rejection-actions">
                  <button
                    onClick={() => handleReview(event._id, 'rejected', rejectionReason)}
                    className="btn btn-danger btn-sm"
                  >
                    Ablehnen
                  </button>
                  <button
                    onClick={() => { setRejectingEventId(null); setRejectionReason(''); }}
                    className="btn btn-secondary btn-sm"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Administrator-Konsole</h1>
        <button className="btn btn-secondary" onClick={() => {
          fetchPendingEvents();
          fetchApprovedEvents();
          fetchRejectedEvents();
          fetchPendingVerifications();
          fetchApprovedVerifications();
          fetchRejectedVerifications();
        }}>
          ↻ Aktualisieren
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          Events
        </button>
        <button
          className={`tab-button ${activeTab === 'verifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('verifications')}
        >
          Verifizierungen {pendingVerifications.length > 0 && `(${pendingVerifications.length})`}
        </button>
      </div>

      

      {activeTab === 'events' ? (
        <>
          <div className="status-filter">
            {[
              { key: 'pending',  label: 'Ausstehend', count: pendingEvents.length },
              { key: 'approved', label: 'Genehmigt',  count: approvedEvents.length },
              { key: 'rejected', label: 'Abgelehnt',  count: rejectedEvents.length },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                className={`status-filter-btn ${eventStatusFilter === key ? 'active' : ''} status-filter-btn--${key}`}
                onClick={() => setEventStatusFilter(key)}
              >
                {label} ({count})
              </button>
            ))}
          </div>
          {currentEvents.length === 0 ? (
            <p className="no-events">
              {eventStatusFilter === 'pending'
                ? 'Keine ausstehenden Events.'
                : eventStatusFilter === 'approved'
                ? 'Keine genehmigten Events.'
                : 'Keine abgelehnten Events.'}
            </p>
          ) : (
            <div className="pending-events">
              {currentEvents.map((event) => renderEventCard(event))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="status-filter">
            {[
              { key: 'pending',  label: 'Ausstehend', count: pendingVerifications.length },
              { key: 'approved', label: 'Genehmigt',  count: approvedVerifications.length },
              { key: 'rejected', label: 'Abgelehnt',  count: rejectedVerifications.length },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                className={`status-filter-btn ${verifStatusFilter === key ? 'active' : ''} status-filter-btn--${key}`}
                onClick={() => setVerifStatusFilter(key)}
              >
                {label} ({count})
              </button>
            ))}
          </div>
          {(verifStatusFilter === 'pending' ? pendingVerifications : verifStatusFilter === 'approved' ? approvedVerifications : rejectedVerifications).length === 0 ? (
            <p className="no-events">
              {verifStatusFilter === 'pending'
                ? 'Keine ausstehenden Verifizierungsanfragen.'
                : verifStatusFilter === 'approved'
                ? 'Keine genehmigten Verifizierungen.'
                : 'Keine abgelehnten Verifizierungen.'}
            </p>
          ) : (
            <div className="verification-requests">
              {(verifStatusFilter === 'pending' ? pendingVerifications : verifStatusFilter === 'approved' ? approvedVerifications : rejectedVerifications)
                .map((userRequest) => renderVerificationCard(userRequest, verifStatusFilter))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminPage;
